import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, X, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const plans = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Get started with basic analytics",
    features: [
      { text: "Last 30 days of data", included: true },
      { text: "Refresh once per day", included: true },
      { text: "Basic metrics", included: true },
      { text: "CSV export", included: false },
      { text: "Extended history", included: false },
      { text: "Weekly email reports", included: false },
    ],
    cta: "Start Free",
    variant: "outline" as const,
    popular: false
  },
  {
    name: "Pro",
    price: "$9.99",
    period: "/month",
    description: "Full analytics for serious traders",
    features: [
      { text: "365 days of data (or all available)", included: true },
      { text: "Unlimited refreshes", included: true },
      { text: "All metrics & charts", included: true },
      { text: "CSV export", included: true },
      { text: "Extended history", included: true },
      { text: "Weekly email reports", included: true },
    ],
    cta: "Upgrade to Pro",
    variant: "hero" as const,
    popular: true
  }
];

export const Pricing = () => {
  return (
    <section className="py-24 md:py-32 relative">
      <div className="container px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Simple, transparent <span className="text-primary">pricing</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Start free. Upgrade when you need more data and features.
          </p>
        </motion.div>

        {/* Pricing cards */}
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className={`relative rounded-2xl border ${
                plan.popular 
                  ? 'border-primary bg-card shadow-lg shadow-primary/10' 
                  : 'border-border bg-card/50'
              } p-8`}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                    <Sparkles className="w-3 h-3" />
                    Most Popular
                  </div>
                </div>
              )}

              {/* Plan header */}
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">{plan.description}</p>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature.text} className="flex items-center gap-3">
                    {feature.included ? (
                      <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-muted-foreground/50 flex-shrink-0" />
                    )}
                    <span className={feature.included ? '' : 'text-muted-foreground/50'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link to={plan.name === "Pro" ? "/dashboard?upgrade=1" : "/dashboard"}>
                <Button 
                  variant={plan.variant} 
                  size="lg" 
                  className="w-full"
                >
                  {plan.cta}
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>
        
        {/* Renewal notice */}
        <div className="text-center mt-8">
          <p className="text-xs text-muted-foreground">
            Plans do not auto-renew. Manual renewal required at end of term.
          </p>
        </div>
      </div>
    </section>
  );
};
